import { type SVGProps } from 'react';
import { cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import { IconComponentStyles } from './icon-component.styles';
import type { IconName } from './icon-constant';
import { multiColorIconComponents, multiColorIconNames } from './multi-color-icon-registry';

export type IconColorType =
  | 'primary'
  | 'default'
  | 'light'
  | 'subdued'
  | 'disabled'
  | 'white'
  | 'error'
  | 'success'
  | 'warn';

const defaultIconSizes = {
  tiny: 10,
  small: 16,
  large: 24,
} as const;

type IconSize = keyof typeof defaultIconSizes;

function determineIconSize(name: IconName, size?: number): number {
  const sizeSuffix = name.match(/-(tiny|small)$/)?.[1] as IconSize | undefined;
  const defaultSize: IconSize = sizeSuffix ? sizeSuffix : 'large';
  return size ?? defaultIconSizes[defaultSize];
}

const idPrefix = 'weaveSpriteIcon-';

type BaseIconProps = Omit<SVGProps<SVGSVGElement>, 'color'> & {
  size?: number;
};

type StandardIconProps = BaseIconProps & {
  name: IconName;
  color?: IconColorType;
  primaryColor?: IconColorType;
  secondaryColor?: IconColorType;
  tertiaryColor?: IconColorType;
};

type MultiColorIconName = Extract<IconName, keyof typeof multiColorIconComponents>;

type MultiColorIconProps = BaseIconProps & {
  name: MultiColorIconName;
  primaryColor?: IconColorType;
  secondaryColor?: IconColorType;
  tertiaryColor?: IconColorType;
  color?: IconColorType;
};

export type IconProps = StandardIconProps;

export function MultiColorIcon({ name, size, color: _, ...props }: MultiColorIconProps) {
  const actualSize = determineIconSize(name, size);
  const MultiColorIconComponent = multiColorIconComponents[name];

  const colorProps: Record<string, string> = {};

  if (props.primaryColor) {
    colorProps.primaryColor = theme.font.colors[props.primaryColor];
  }
  if (props.secondaryColor) {
    colorProps.secondaryColor = theme.font.colors[props.secondaryColor];
  }
  if (props.tertiaryColor) {
    colorProps.tertiaryColor = theme.font.colors[props.tertiaryColor];
  }

  return (
    <MultiColorIconComponent colors={theme.colors} width={actualSize} height={actualSize} {...props} {...colorProps} />
  );
}

export function Icon({ name, size, ...props }: IconProps) {
  const actualSize = determineIconSize(name, size);

  if (multiColorIconNames.has(name)) {
    const multiColorIconName = name as MultiColorIconName;
    if (multiColorIconComponents[multiColorIconName]) {
      return <MultiColorIcon size={size} {...(props as MultiColorIconProps)} name={multiColorIconName} />;
    }
  }

  const standardProps = props as StandardIconProps;
  const iconColor = standardProps.color ? theme.font.colors[standardProps.color] : undefined;

  return (
    <svg
      width={actualSize}
      height={actualSize}
      {...props}
      style={
        iconColor
          ? {
              color: iconColor,
              ...props.style,
            }
          : props.style
      }
      className={cx(IconComponentStyles.iconSvgStyle1, props.className)}
    >
      <use href={`#${idPrefix}${name}`} />
    </svg>
  );
}
